/**
 * FNV-1a (32-bit) hashing over numbers, for desync detection and determinism tests.
 *
 * Every number is hashed through its IEEE-754 float64 bit pattern (DataView, little-endian), so 0.1 + 0.2 and 0.3
 * hash differently: the hash detects ANY bit drift. NaN is canonicalized (all NaNs hash the same); -0 and +0 are
 * kept distinct because a peer producing -0 where another produced +0 is real divergence.
 */

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

const scratch = new DataView(new ArrayBuffer(8));
const CANONICAL_NAN_HI = 0x7ff80000;

/** Incremental FNV-1a hasher. Reuse one instance; call `reset()` between hashes. */
export class Fnv1a {
  h = FNV_OFFSET;

  reset(seed: number = FNV_OFFSET): this {
    this.h = seed >>> 0;
    return this;
  }

  byte(b: number): this {
    this.h = Math.imul(this.h ^ (b & 0xff), FNV_PRIME) >>> 0;
    return this;
  }

  u32(v: number): this {
    let h = this.h;
    h = Math.imul(h ^ (v & 0xff), FNV_PRIME);
    h = Math.imul(h ^ ((v >>> 8) & 0xff), FNV_PRIME);
    h = Math.imul(h ^ ((v >>> 16) & 0xff), FNV_PRIME);
    h = Math.imul(h ^ ((v >>> 24) & 0xff), FNV_PRIME);
    this.h = h >>> 0;
    return this;
  }

  /** Hash a number by its float64 bits. */
  num(v: number): this {
    if (v !== v) {
      this.u32(0);
      return this.u32(CANONICAL_NAN_HI);
    }
    scratch.setFloat64(0, v, true);
    this.u32(scratch.getUint32(0, true));
    return this.u32(scratch.getUint32(4, true));
  }

  bool(v: boolean): this {
    return this.byte(v ? 1 : 0);
  }

  /** Hash a string by UTF-16 code units (not for the hot path). */
  str(s: string): this {
    this.u32(s.length);
    for (let i = 0; i < s.length; i++) this.u32(s.charCodeAt(i));
    return this;
  }

  digest(): number {
    return this.h >>> 0;
  }
}

const shared = new Fnv1a();

/** Hash a list of numbers (float64 bits). */
export const hashNumbers = (values: ArrayLike<number>, seed: number = FNV_OFFSET): number => {
  shared.reset(seed);
  for (let i = 0; i < values.length; i++) shared.num(values[i]!);
  return shared.digest();
};

/** Hash raw bytes. */
export const hashBytes = (bytes: Uint8Array, seed: number = FNV_OFFSET): number => {
  shared.reset(seed);
  for (let i = 0; i < bytes.length; i++) shared.byte(bytes[i]!);
  return shared.digest();
};

/**
 * Hash any plain-data value (numbers, booleans, strings, null/undefined, arrays, plain objects) by walking it in
 * property-insertion order. Objects built by the same factory have the same key order, so this is stable across
 * peers. Key NAMES are not hashed (only values, in order) to keep it fast. Intended for SimState (`@hf/sim`
 * hashState uses it) and tests. Throws on functions/symbols/bigints. Cycles are not detected: SimState is a tree by
 * contract.
 */
export const hashPlain = (value: unknown, hasher: Fnv1a = new Fnv1a()): number => {
  walk(value, hasher);
  return hasher.digest();
};

const walk = (v: unknown, h: Fnv1a): void => {
  switch (typeof v) {
    case 'number':
      h.byte(1).num(v);
      return;
    case 'boolean':
      h.byte(2).bool(v);
      return;
    case 'string':
      h.byte(3).str(v);
      return;
    case 'undefined':
      h.byte(4);
      return;
    case 'object': {
      if (v === null) {
        h.byte(5);
        return;
      }
      if (Array.isArray(v)) {
        h.byte(6).u32(v.length);
        for (let i = 0; i < v.length; i++) walk(v[i], h);
        return;
      }
      if (ArrayBuffer.isView(v) && !(v instanceof DataView)) {
        const arr = v as unknown as ArrayLike<number>;
        h.byte(7).u32(arr.length);
        for (let i = 0; i < arr.length; i++) h.num(arr[i]!);
        return;
      }
      h.byte(8);
      const o = v as Record<string, unknown>;
      for (const k in o) walk(o[k], h);
      return;
    }
    default:
      throw new TypeError(`hashPlain: unsupported value of type ${typeof v}`);
  }
};

/** Format a hash for logs/HUD (8 hex digits). */
export const formatHash = (h: number): string => (h >>> 0).toString(16).padStart(8, '0');
